/**
 * Queries for the Metrics overhaul (2026-10-22). See `../cleaning-metrics.ts`
 * for the shapes and the "older api-server" answers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { METRICS_KEY } from '../scope';
import {
  fetchCleaningMetrics,
  fetchLiftMetrics,
  fetchSiteMetricSettings,
  saveSiteMetricSettings,
  type MetricsWindowQuery,
} from '../v1-client';
import {
  adaptCleaningMetrics,
  adaptLiftMetrics,
  adaptSiteMetricSettings,
  type CleaningMetrics,
  type LiftMetrics,
  type SiteMetricSettings,
  type SiteMetricSettingsUpdate,
} from '../cleaning-metrics';
import type { SiteId } from '../v1';

export const CLEANING_METRICS_KEY = 'metrics-cleaning' as const;
export const LIFT_METRICS_KEY = 'metrics-lifts' as const;
export const SITE_METRIC_SETTINGS_KEY = 'site-metric-settings' as const;

/** `data === null`: this api-server has no cleaning read yet. */
export function useCleaningMetrics(q: MetricsWindowQuery, enabled = true) {
  const siteId = useSafeSiteId();
  return useQuery<CleaningMetrics | null>({
    queryKey: [CLEANING_METRICS_KEY, siteId, q.from, q.to, q.compareFrom ?? null, q.compareTo ?? null],
    queryFn: async () => {
      const raw = await fetchCleaningMetrics(siteId as SiteId, q);
      return raw === null ? null : adaptCleaningMetrics(raw);
    },
    enabled: Boolean(siteId) && enabled,
    staleTime: 60_000,
  });
}

/** `data === null`: no lift and door figures (no endpoint, or an empty body). */
export function useLiftMetrics(q: MetricsWindowQuery, enabled = true) {
  const siteId = useSafeSiteId();
  return useQuery<LiftMetrics | null>({
    queryKey: [LIFT_METRICS_KEY, siteId, q.from, q.to],
    queryFn: async () => adaptLiftMetrics(await fetchLiftMetrics(siteId as SiteId, q)),
    enabled: Boolean(siteId) && enabled,
    staleTime: 60_000,
    // A missing endpoint is an answer, not a failure to retry.
    retry: 0,
  });
}

export function useSiteMetricSettings() {
  const siteId = useSafeSiteId();
  return useQuery<SiteMetricSettings | null>({
    queryKey: [SITE_METRIC_SETTINGS_KEY, siteId],
    queryFn: async () => {
      const raw = await fetchSiteMetricSettings(siteId as SiteId);
      return raw === null ? null : adaptSiteMetricSettings(raw);
    },
    enabled: Boolean(siteId),
    staleTime: 30_000,
  });
}

export function useSaveSiteMetricSettings() {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  return useMutation<SiteMetricSettings, Error, SiteMetricSettingsUpdate>({
    mutationFn: async (body) =>
      adaptSiteMetricSettings(await saveSiteMetricSettings(siteId as SiteId, body)),
    onSuccess: (saved) => {
      qc.setQueryData([SITE_METRIC_SETTINGS_KEY, siteId], saved);
      // Money, targets and groups all change what the cleaning read says;
      // the go-live date ("Count metrics from") changes every Metrics read.
      void qc.invalidateQueries({ queryKey: [CLEANING_METRICS_KEY, siteId] });
      void qc.invalidateQueries({ queryKey: [LIFT_METRICS_KEY, siteId] });
      void qc.invalidateQueries({ queryKey: [METRICS_KEY, siteId] });
    },
  });
}
